import {
  WSActions,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { calculateMonthProratedAmount } from "@wingspanhq/utils/dist/date/banking";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  DefaultPaymentMethod,
  useDefaultPaymentMethod
} from "../../../../Settings/components/payment/DefaultPaymentMethod";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD } from "../../../../Settings/paths";
import { PGLI_KEY, PLAN_DETAILS } from "../../../utils/constants";
import { useModalEditDefaultPaymentMethod } from "./modals/useEditDefaultPaymentMethod";
import { FlowStep } from "../../../../../shared/components/FlowSwitch/types";

const {
  monthlyPremiumFormatted,
  initiationFeeFormatted,
  monthlyPremium,
  initiationFee,
  name
} = PLAN_DETAILS[PGLI_KEY];

export const StepPaymentMethod: React.FC<FlowStep> = props => {
  const history = useHistory();
  const location = useLocation();
  const modalEditDefaultPaymentMethod = useModalEditDefaultPaymentMethod();
  // TODO(v3-blocker): a member's own default payment method lived on the v1 client
  // profile. v3 carries one per payer relationship (`PayerSettings`) and a
  // read-only one per contractor (`Payee`), but none on the account — so the
  // premium method can be neither read nor written, and this step's "Continue"
  // stays disabled.
  const defaultPaymentMethod = useDefaultPaymentMethod();

  const [futureFeeMethod, setFutureFeeMethod] = React.useState<
    "deductions" | "payment"
  >("deductions");

  const { monthName, nextMonthName, prorated, total } = useMemo(() => {
    const now = new Date();
    const monthName = now.toLocaleString("en-US", { month: "long" });
    const nextMonthName = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      1
    ).toLocaleString("en-US", { month: "long" });

    const prorated = calculateMonthProratedAmount(now, monthlyPremium);

    const total = prorated + monthlyPremium + initiationFee!;

    return {
      monthName,
      nextMonthName,
      prorated,
      total
    };
  }, []);

  return (
    <WSPanel>
      <WSList gap="2XL">
        <WSElement shimmer={defaultPaymentMethod.isLoading}>
          <WSText.Paragraph weight="medium" mb="XL">
            Payment method
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500" mb="L">
            Confirm your payment method or add a new one
          </WSText.ParagraphSm>

          <DefaultPaymentMethod
            helperText={""}
            onAddNew={() => {
              history.push({
                pathname: PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD,
                state: {
                  backPath: location.pathname
                }
              });
            }}
            onManage={() => {
              modalEditDefaultPaymentMethod.open();
            }}
          />
        </WSElement>

        {/* Your first payment block */}
        <WSCard>
          <WSList gap="M">
            <WSText.Paragraph color="gray500" weight="medium">
              Your first payment:
            </WSText.Paragraph>
            <WSText.ParagraphXs color="gray500" mb="L">
              The amount that will be charged to your default payment method
              when you complete enrollment.
            </WSText.ParagraphXs>

            <WSList gap="M">
              <WSFlexBox.CenterY justify="space-between">
                <WSText.ParagraphSm color="gray500">
                  Current month: {monthName} (prorated)
                </WSText.ParagraphSm>

                <WSText.ParagraphSm>
                  {formatMoney(prorated, "USD")}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>

              <WSFlexBox.CenterY justify="space-between">
                <WSText.ParagraphSm color="gray500">
                  Next month: {nextMonthName}
                </WSText.ParagraphSm>

                <WSText.ParagraphSm>
                  {formatMoney(monthlyPremium, "USD")}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>

              <WSFlexBox.CenterY justify="space-between">
                <WSText.ParagraphSm color="gray500">
                  Setup fee:
                </WSText.ParagraphSm>
                <WSText.ParagraphSm>
                  {formatMoney(initiationFee || 0, "USD")}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
              <WSDivider />

              <WSFlexBox.CenterY justify="space-between">
                <WSText.ParagraphSm weight="medium">Total:</WSText.ParagraphSm>
                <WSText.ParagraphSm>
                  {formatMoney(total, "USD")}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
            </WSList>
          </WSList>
        </WSCard>

        {/* Future monthly fees block
        <WSCard>
          <WSList gap="M">
            <WSText.Paragraph weight="medium" color="gray500">
              Future monthly fees:
            </WSText.Paragraph>
            <WSText.ParagraphXs color="gray500">
              Choose between deductions or payment method for future monthly
              fees after your first payment.
            </WSText.ParagraphXs>

            <WSControlGroup
              type="radio"
              value="deductions"
              name="futureFeeMethod"
              mb="L"
              options={[
                {
                  helperText: `Automatically deduct your monthly fee from your earnings. We'll do the math so you don't have to. (Recommended — preferred by most contractors)*`,
                  label: "Deductions",
                  value: "deductions"
                },
                {
                  label: "Payment method",
                  helperText:
                    "Pay my monthly fee using my default payment method",
                  value: "payment"
                }
              ]}
              onChange={() => setFutureFeeMethod("deductions")}
            />

            <WSInfoBox title="*About deductions" color="gray500">
              To maintain your coverage, if we aren't able to deduct the{" "}
              {formatMoney(PGLI_MONTH_FEE, "USD")} fee from invoices in a given
              month, we will fall back on your default payment method. You will
              never be charged more than the {formatMoney(PGLI_MONTH_FEE, "USD")}{" "}
              on a monthly basis.
            </WSInfoBox>
          </WSList>
        </WSCard>
        */}

        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              onClick: props.onBack,
              label: "Back"
            },
            {
              disabled: !defaultPaymentMethod.id,
              label: "Continue",
              type: "submit",
              onClick: props.onNext
            }
          ]}
        />
      </WSList>
    </WSPanel>
  );
};
